import { useEffect, useState } from "react";
import { toast } from "sonner";
import { opsPushTest, opsDigestNow, opsUnlockMessageGet, opsUnlockMessageSet, opsWebhookTest } from "@/lib/api";

/**
 * Owner-only ops card on the Admin page.
 * Contains three actions:
 *   1. Push preview test — dispatches a test notification to the owner's devices
 *   2. Digest preview — fires the Monday weekly digest email right now
 *   3. Unlock message editor — customize the T-7 payments-unlock email subject + body
 */
export const OwnerOpsCard = () => {
  const [busy, setBusy] = useState(null); // 'push' | 'digest' | 'save' | null
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    opsUnlockMessageGet()
      .then((r) => { setSubject(r.subject || ""); setBody(r.body_html || ""); })
      .catch(() => {});
  }, []);

  const runPushTest = async () => {
    setBusy("push");
    try {
      const r = await opsPushTest();
      toast.success(r?.message || "Push dispatched");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Push failed — check device permissions");
    } finally { setBusy(null); }
  };

  const runDigestNow = async () => {
    setBusy("digest");
    try {
      const r = await opsDigestNow();
      toast.success(r?.message || "Digest sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Digest failed");
    } finally { setBusy(null); }
  };

  const runWebhookTest = async () => {
    setBusy("webhook");
    try {
      const r = await opsWebhookTest();
      toast.success(r?.message || "Webhook dispatched");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Webhook failed — set NOTIFY_WEBHOOK_URL in .env");
    } finally { setBusy(null); }
  };

  const saveUnlockMessage = async () => {
    setBusy("save");
    try {
      await opsUnlockMessageSet(subject, body);
      toast.success("Unlock email saved — the cron loop will use it on next fire");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  return (
    <section data-testid="owner-ops-card" className="border border-[#a855f7]/40 bg-[#a855f7]/5 p-5 space-y-6">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono">// owner ops · previews &amp; overrides</div>
        <div className="text-sm text-zinc-200 mt-1">Fire test notifications and customize scheduled emails.</div>
      </div>

      {/* Push + Digest + Webhook quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          data-testid="ops-push-test-btn"
          onClick={runPushTest}
          disabled={busy === "push"}
          className="border border-[#22c55e]/50 hover:bg-[#22c55e]/10 text-[#22c55e] px-4 py-3 text-[10px] tracking-[0.3em] uppercase font-mono font-bold text-left disabled:opacity-50 transition-colors"
        >
          {busy === "push" ? "Dispatching…" : "🔔 Send push test to my devices →"}
        </button>
        <button
          data-testid="ops-digest-now-btn"
          onClick={runDigestNow}
          disabled={busy === "digest"}
          className="border border-[#60a5fa]/50 hover:bg-[#60a5fa]/10 text-[#60a5fa] px-4 py-3 text-[10px] tracking-[0.3em] uppercase font-mono font-bold text-left disabled:opacity-50 transition-colors"
        >
          {busy === "digest" ? "Sending…" : "📊 Send weekly digest now →"}
        </button>
        <button
          data-testid="ops-webhook-test-btn"
          onClick={runWebhookTest}
          disabled={busy === "webhook"}
          className="border border-[#facc15]/50 hover:bg-[#facc15]/10 text-[#facc15] px-4 py-3 text-[10px] tracking-[0.3em] uppercase font-mono font-bold text-left disabled:opacity-50 transition-colors"
        >
          {busy === "webhook" ? "Dispatching…" : "💬 Send test webhook →"}
        </button>
      </div>

      {/* Unlock message editor */}
      <div className="border-t border-zinc-800 pt-5">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono mb-3">// t-7 payments-unlock email</div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono block mb-1">Subject</label>
            <input
              data-testid="ops-unlock-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              className="w-full bg-black border border-zinc-700 focus:border-[#a855f7] outline-none px-3 py-2 text-white text-sm font-mono"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono block mb-1">
              Body HTML · use <code className="text-[#facc15]">{"{{UNLOCK_DATE}}"}</code> and <code className="text-[#facc15]">{"{{SHARE_URL}}"}</code>
            </label>
            <textarea
              data-testid="ops-unlock-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              maxLength={5000}
              className="w-full bg-black border border-zinc-700 focus:border-[#a855f7] outline-none px-3 py-2 text-white text-xs font-mono resize-y"
            />
          </div>
          <button
            data-testid="ops-unlock-save-btn"
            onClick={saveUnlockMessage}
            disabled={busy === "save"}
            className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#a855f7] hover:bg-[#c084fc] px-4 py-2 font-black disabled:opacity-50 transition-colors"
          >
            {busy === "save" ? "Saving…" : "Save unlock message"}
          </button>
        </div>
      </div>
    </section>
  );
};
